import { PageDataTable } from "components/PageDataTable/PageDataTable";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTablePFSEvent } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { Tag } from "primereact/tag";
import { Tooltip } from "primereact/tooltip";
import { useMemo, useState } from "react";
import { useGetAllAccountsQuery } from "store/queries/account";
import { useGetHstImportBatchesQuery } from "store/queries/integration";
import { IHstImportBatchSummary } from "types/HstImportBatch";

import {
  formatDateTime,
  totalDuplicates,
  userDisplayName,
} from "../../utils/hstImports";
import { HstImportDetailDialog } from "./HstImportDetailDialog";
import { HstImportFileLinks } from "./HstImportFileLinks";
import { UndoHstImportDialog } from "./UndoHstImportDialog";

const PAGE_SIZE = 25;
const TOOLTIP_CLASS = "hst-import-tooltip";

const importedBody = (b: IHstImportBatchSummary) => formatDateTime(b.createdAt);

const importedByBody = (b: IHstImportBatchSummary) =>
  userDisplayName(b.importedBy);

const duplicatesBody = (b: IHstImportBatchSummary) => (
  <span
    className={TOOLTIP_CLASS}
    data-pr-tooltip={`${b.duplicateCount} already existed, ${b.csvDuplicateCount} repeated within the file`}
  >
    {totalDuplicates(b)}
  </span>
);

const statusBody = (b: IHstImportBatchSummary) => {
  if (b.status === "Undone") {
    const who = userDisplayName(b.undoneBy);
    return (
      <div className="flex flex-column gap-1">
        <Tag
          value="Undone"
          icon="pi pi-undo"
          style={{ background: "var(--gray-500)" }}
        />
        <small className="text-600">
          {formatDateTime(b.undoneAt)}
          {who ? ` by ${who}` : ""}
        </small>
      </div>
    );
  }
  if (!b.undoable) {
    return (
      <span
        className={TOOLTIP_CLASS}
        data-pr-tooltip={
          b.undoBlockers.length
            ? b.undoBlockers.join("\n")
            : "Outside the undo window"
        }
      >
        <Tag value="Locked" severity="warning" icon="pi pi-lock" />
      </span>
    );
  }
  return <Tag value="Active" severity="success" />;
};

export function HstImportsTab() {
  const [accountId, setAccountId] = useState<string | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [detailBatchId, setDetailBatchId] = useState<string | null>(null);
  const [undoBatch, setUndoBatch] = useState<IHstImportBatchSummary | null>(
    null
  );

  const { data: accounts } = useGetAllAccountsQuery(null);
  const accountOptions = useMemo(
    () =>
      [...(accounts?.data || [])]
        .map((account) => ({ label: account.name, value: account.id }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [accounts]
  );

  const { data, isFetching } = useGetHstImportBatchesQuery({
    accountId,
    page,
    pageSize: PAGE_SIZE,
  });
  const list = data?.data;
  const items = list?.items || [];

  const onPage = (event: DataTablePFSEvent) => {
    setPage(Math.floor(event.first / event.rows) + 1);
  };

  const filesBody = (b: IHstImportBatchSummary) => (
    <HstImportFileLinks batch={b} />
  );

  const actionsBody = (b: IHstImportBatchSummary) => (
    <div className="flex gap-1">
      <Button
        label="Details"
        className="p-button-text p-button-sm"
        onClick={() => setDetailBatchId(b.id)}
      />
      <Button
        label="Undo"
        icon="pi pi-undo"
        className="p-button-text p-button-sm p-button-danger"
        disabled={!b.undoable || b.status !== "Active"}
        onClick={() => setUndoBatch(b)}
        data-testid={`hst-import-undo-${b.id}`}
      />
    </div>
  );

  return (
    <div className="flex flex-column gap-3">
      <Tooltip target={`.${TOOLTIP_CLASS}`} position="top" />
      <div className="flex flex-wrap align-items-center justify-content-between gap-3">
        <Dropdown
          value={accountId}
          options={accountOptions}
          onChange={(e) => {
            setAccountId(e.value || undefined);
            setPage(1);
          }}
          placeholder="All accounts"
          filter
          showClear
          style={{ minWidth: "280px" }}
          aria-label="Filter by account"
        />
        {list?.windowDays ? (
          <small className="text-600">
            Imports can be undone for {list.windowDays} days.
          </small>
        ) : null}
      </div>

      <PageDataTable
        value={items}
        dataKey="id"
        lazy
        paginator
        first={(page - 1) * PAGE_SIZE}
        rows={PAGE_SIZE}
        totalRecords={list?.total || 0}
        onPage={onPage}
        loading={isFetching}
        emptyMessage="No HST imports yet."
      >
        <Column header="Imported" body={importedBody} />
        <Column header="Account" field="accountName" />
        <Column header="Imported by" body={importedByBody} />
        <Column header="Files" body={filesBody} />
        <Column header="Created" field="createdCount" />
        <Column header="Updated" field="updatedCount" />
        <Column header="Errors" field="errorCount" />
        <Column header="Duplicates" body={duplicatesBody} />
        <Column header="Status" body={statusBody} />
        <Column header="" body={actionsBody} style={{ width: "12rem" }} />
      </PageDataTable>

      <HstImportDetailDialog
        batchId={detailBatchId}
        onHide={() => setDetailBatchId(null)}
      />
      <UndoHstImportDialog
        batch={undoBatch}
        onHide={() => setUndoBatch(null)}
      />
    </div>
  );
}
